import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';
import { locationApi } from 'components/location/LocationApi';
import { isLocations, Location, LocationState } from 'components/location/models/LocationModel';
import {
    MESSAGE_LOCATION_ACTIVATE_ERROR,
    MESSAGE_LOCATION_ACTIVATE_SUCCESS,
    MESSAGE_LOCATION_DELETE_ERROR,
    MESSAGE_LOCATION_DELETE_SUCCESS,
    MESSAGE_LOCATION_RETIRE_ERROR,
    MESSAGE_LOCATION_RETIRE_SUCCESS
} from 'config/messages';
import logger from 'lib/logger';
import { enqueueSnackbar } from 'notistack';
import { updateArrayItem } from 'lib/helpers';
import type { AppDispatch } from '../store';

const LOCATIONS_PER_PAGE = 100;
const LOCATION_STATES = 'ACTIVE,PENDING,RETIRED';

export interface LocationsState {
    locations?: Location[];
    nextKey?: string;
    error: string;
    isLoading: boolean;
    locationIdToLocationTitle?: Map<string, string>;
}

export const initialState: LocationsState = {
    locations: [],
    locationIdToLocationTitle: new Map(),
    error: null,
    nextKey: null,
    isLoading: false
};

const locationsSlice = createSlice({
    name: 'locations',
    initialState,
    reducers: {
        startRequest(state) {
            state.error = null;
            state.isLoading = true;
        },
        endRequest(state) {
            state.error = null;
            state.isLoading = false;
        },
        getLocationsSuccess(
            state,
            action: PayloadAction<{
                locations: Location[];
                nextKey?: string;
            }>
        ) {
            state.locations = action.payload.locations;
            state.nextKey = action.payload.nextKey;
            state.error = null;
        },
        getAllLocationTitlesSuccess(
            state,
            action: PayloadAction<{
                locations: Location[];
                locationIdToTitleMap: Map<string, string>;
            }>
        ) {
            state.locations = action.payload.locations;
            state.locationIdToLocationTitle = action.payload.locationIdToTitleMap;
        },
        getLocationsError(state, action: PayloadAction<{ error: string }>) {
            state.error = action.payload.error;
            state.locations = null;
            state.nextKey = null;
        },
        loadLocationsSuccess(
            state,
            action: PayloadAction<{
                locations: Location[];
                nextKey?: string;
            }>
        ) {
            state.nextKey = action.payload.nextKey;
            state.error = null;
            state.locations = [...state.locations, ...action.payload.locations];
        },
        deleteLocationByIndex(state, action: PayloadAction<{ index: number }>) {
            const { index } = action.payload;
            state.locations = [...state.locations.slice(0, index), ...state.locations.slice(index + 1)];
        },
        updateLocation(state, action: PayloadAction<{ index: number; location: Location }>) {
            const { index, location } = action.payload;
            state.locations = updateArrayItem(state.locations, index, location);
        }
    }
});

export const {
    startRequest,
    endRequest,
    getLocationsSuccess,
    getAllLocationTitlesSuccess,
    getLocationsError,
    loadLocationsSuccess,
    deleteLocationByIndex,
    updateLocation
} = locationsSlice.actions;

// --- Thunks ---

export const getAllLocationTitles = async (dispatch: AppDispatch) => {
    const allLocations: Location[] = await locationApi.getAllLocations({
        state: LOCATION_STATES,
        limit: LOCATIONS_PER_PAGE
    });
    const locationIdToTitleMap = allLocations.reduce((acc, location) => {
        acc.set(location._id, location.title);

        return acc;
    }, new Map());

    dispatch(
        getAllLocationTitlesSuccess({
            locations: allLocations,
            locationIdToTitleMap
        })
    );
    return allLocations;
};

export const getLocations = (title?: string) => async (dispatch: AppDispatch) => {
    dispatch(startRequest());
    const response = await locationApi.getLocationsPage({
        state: LOCATION_STATES,
        limit: LOCATIONS_PER_PAGE,
        title,
        include: ['address', 'description']
    });
    const { items, page } = response.body;
    if (isLocations(items)) {
        dispatch(getLocationsSuccess({ locations: items, nextKey: page?.nextKey }));
    } else {
        logger.error('TypeError: expected Location[], got:', items);
        dispatch(getLocationsError({ error: 'Locations data violated' }));
    }
    dispatch(endRequest());
};

export const loadLocations = (nextKey?: string, title?: string) => async (dispatch: AppDispatch) => {
    dispatch(startRequest());
    if (!nextKey) {
        return dispatch(endRequest());
    }
    const response = await locationApi.getLocationsPage({
        state: LOCATION_STATES,
        limit: LOCATIONS_PER_PAGE,
        startKey: nextKey,
        title,
        include: ['address', 'description']
    });
    const { items, page } = response.body;
    if (isLocations(items)) {
        dispatch(loadLocationsSuccess({ locations: items, nextKey: page?.nextKey }));
    } else {
        logger.error('TypeError: expected Location[], got:', items);
        dispatch(getLocationsError({ error: 'Locations data violated' }));
    }
    dispatch(endRequest());
};

export const getAllLocations = () => async (dispatch: AppDispatch) => {
    dispatch(startRequest());
    try {
        const locations = await locationApi.getAllLocations({
            state: LOCATION_STATES,
            limit: LOCATIONS_PER_PAGE
        });
        if (isLocations(locations)) {
            dispatch(getLocationsSuccess({ locations, nextKey: null }));
        } else {
            logger.error('TypeError: expected Location[], got:', locations);
            dispatch(getLocationsError({ error: 'Locations data violated' }));
        }
        dispatch(endRequest());

        return locations;
    } catch (e) {
        logger.error(e);
        dispatch(getLocationsError({ error: 'Locations data violated' }));
        dispatch(endRequest());
        return [];
    }
};

export const retireLocation = (location: Location, index: number) => async (dispatch: Dispatch) => {
    const { _id, ...rest } = location;
    dispatch(startRequest());
    const response = await locationApi.update(_id, { body: { ...rest, state: LocationState.RETIRED } });
    if (response.ok) {
        enqueueSnackbar(MESSAGE_LOCATION_RETIRE_SUCCESS(location.title), { variant: 'success' });
        dispatch(updateLocation({ index, location: response.body }));
    } else {
        enqueueSnackbar(MESSAGE_LOCATION_RETIRE_ERROR, { variant: 'error' });
    }
    dispatch(endRequest());
};

export const activateLocation = (location: Location, index: number) => async (dispatch: Dispatch) => {
    const { _id, ...rest } = location;
    dispatch(startRequest());
    const response = await locationApi.update(_id, { body: { ...rest, state: LocationState.ACTIVE } });
    if (response.ok) {
        enqueueSnackbar(MESSAGE_LOCATION_ACTIVATE_SUCCESS(location.title), { variant: 'success' });
        dispatch(updateLocation({ index, location: response.body }));
    } else {
        enqueueSnackbar(MESSAGE_LOCATION_ACTIVATE_ERROR, { variant: 'error' });
    }
    dispatch(endRequest());
};

export const deleteLocation = (locationId: string, index: number) => async (dispatch: Dispatch) => {
    dispatch(startRequest());
    const response = await locationApi.delete(locationId, { skipResponseBody: true });
    if (response.ok) {
        enqueueSnackbar(MESSAGE_LOCATION_DELETE_SUCCESS, { variant: 'success' });
        dispatch(deleteLocationByIndex({ index }));
    } else {
        enqueueSnackbar(MESSAGE_LOCATION_DELETE_ERROR, { variant: 'error' });
    }
    dispatch(endRequest());
};

export default locationsSlice.reducer;
